import React from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from './../../../../api';
import { useSelector } from 'react-redux';
import { toastrAdd } from '../../../../actions/ToastrActions';
import { useDispatch } from 'react-redux';
import { userSaveSuccess } from '../../../../actions';
import { useTranslation } from 'react-i18next';

function UserForm(props) {
	const dispatch = useDispatch();
	const { user, loading, saving, callback } = props;
	const { userID } = useSelector(({ user }) => user);
	const { t } = useTranslation();

	const showSaveError = () => dispatch(toastrAdd({ code: 0, details: t('form.check_form_fields'), title: t('form.data_not_saved'), type: 'error' }));
	const showSaveSuccess = () => dispatch(toastrAdd({ type: 'success', title: t('users.save_success_title'), details: t('users.save_success_details') }));

	const onSubmit = (values) => {
		values = {
			...values,
			partnerID: localStorage.getItem('partnerID')
		};

		if (values.userID) {
			const isCurrentUser = values.userID === userID;

			api.user.saveUser(values.userID, values)
				.then((response) => {
					dispatch(userSaveSuccess(response.data.data.user, isCurrentUser));
					showSaveSuccess();
					callback();
				})
				.catch(error => {
					showSaveError();
				});
		} else {
			api.user.createPartnerUser(values)
				.then(callback)
				.catch(({ data }) => {
					if (data.errors && data.errors[0].code === 2000 && data.errors[0].details === 'Email already in use') {
						api.user.linkUserToPartner({ email: values.email, partnerID: values.partnerID })
							.then(() => {
								showSaveSuccess();
								callback();
							})
							.catch(showSaveError);
					} else {
						showSaveError();
					}
				});
		}
	};

	const formik = useFormik({
		initialValues: { ...user },
		enableReinitialize: true,
		validationSchema: Yup.object().shape({
			firstNames: Yup.string()
				.required(t('form.first_name_required')),
			lastName: Yup.string()
				.required(t('form.last_name_required')),
			email: Yup.string()
				.required(t('form.email_required'))
				.email(t('form.email_invalid')),
			phone: Yup.string()
				.min(7, t('form.7_characters'))
				.nullable(),
			...(user && user.userID ? {
				password: Yup.string().min(6)
			} : {
					password: Yup.string()
						.required()
						.min(6, t('form.6_characters'))
				}
			)
		}),
		onSubmit: onSubmit
	});

	const { values, errors, touched } = formik;

	return (
		<Form onSubmit={formik.handleSubmit}>
			<Form.Group as={Row} controlId="firstNames" className="required">
				<Form.Label column sm={3}>{t('general.first_name')}</Form.Label>
				<Col sm={9}>
					<Form.Control
						value={values.firstNames || ''}
						onChange={formik.handleChange}
						disabled={saving}
						isInvalid={errors.firstNames && touched.firstNames}
						required
					/>
					{errors.firstNames && touched.firstNames ? (
						<Form.Control.Feedback type="invalid">
							{errors.firstNames}
						</Form.Control.Feedback>
					) : null}
				</Col>
			</Form.Group>
			<Form.Group as={Row} controlId="lastName" className="required">
				<Form.Label column sm={3}>{t('general.last_name')}</Form.Label>
				<Col sm={9}>
					<Form.Control
						value={values.lastName || ''}
						onChange={formik.handleChange}
						disabled={saving}
						isInvalid={errors.lastName && touched.lastName}
						required
					/>
					{errors.lastName && touched.lastName ? (
						<Form.Control.Feedback type="invalid">
							{errors.lastName}
						</Form.Control.Feedback>
					) : null}
				</Col>
			</Form.Group>
			<Form.Group as={Row} controlId="email" className="required">
				<Form.Label column sm={3}>{t('general.email')}</Form.Label>
				<Col sm={9}>
					<Form.Control
						value={values.email || ''}
						onChange={formik.handleChange}
						disabled={saving}
						isInvalid={errors.email && touched.email}
						required
					/>
					{errors.email && touched.email ? (
						<Form.Control.Feedback type="invalid">
							{errors.email}
						</Form.Control.Feedback>
					) : null}
				</Col>
			</Form.Group>
			<Form.Group as={Row} controlId="phone">
				<Form.Label column sm={3}>{t('general.phone_number')}</Form.Label>
				<Col sm={9}>
					<Form.Control
						value={values.phone || ''}
						onChange={formik.handleChange}
						isInvalid={errors.phone && touched.phone}
						disabled={saving}
					/>
					{errors.phone && touched.phone ? (
						<Form.Control.Feedback type="invalid">
							{errors.phone}
						</Form.Control.Feedback>
					) : null}
				</Col>
			</Form.Group>
			<Form.Group as={Row} controlId="password" className={!values.userID ? 'required' : ''}>
				<Form.Label column sm={3}>{t('general.password')}</Form.Label>
				<Col sm={6}>
					<Form.Control
						value={values.password || ''}
						onChange={formik.handleChange}
						isInvalid={errors.password && touched.password}
						disabled={saving}
						required={!values.userID}
					/>
					{errors.password && touched.password ? (
						<Form.Control.Feedback type="invalid">
							{errors.password}
						</Form.Control.Feedback>
					) : null}
				</Col>
			</Form.Group>
			<Form.Group as={Row}>
				<Col sm={{ span: 9, offset: 3 }}>
					<Button
						type="submit"
						variant="success"
						disabled={(loading || saving)}
					>
						{t('general.save_changes')}
					</Button>
				</Col>
			</Form.Group>
		</Form>
	)
}

export default UserForm;